import React, { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import WarningIcon from '@mui/icons-material/WarningRounded';
import InfoIcon from '@mui/icons-material/Info';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Button } from 'components/Shared/Inputs';
import { useCredentialing } from '../../Tabs/Credentialing/CredentialingContext';
import { type License } from 'components/ProviderProfile/interfaces';

import './LicensesAndRegistrations.scss';

export type LicensesAndRegistrationsAlertTypes = 'expired' | 'expiring';

interface LicensesAndRegistrationsAlertProps {
    type: LicensesAndRegistrationsAlertTypes
    onRenewClick: (licenseId: number) => void;
}

interface SingleLicenseAlertProps extends LicensesAndRegistrationsAlertProps {
    license?: License;
}

interface MultiLicenseAlertProps extends LicensesAndRegistrationsAlertProps {
    licenses: License[];
}

const SingleLicenseAlertBody: FC<SingleLicenseAlertProps> = ({ license, type, onRenewClick }) => {
    const { t } = useTranslation(['providerprofile']);
    if (!license) return null;
    const { licenseRegistrationAssociation } = license;
    return (
        <div className="alert-content">
            <div className="alert-text">
                <div className="message-line">
                    <div className="message">
                        {
                            type === 'expiring'
                                ? t("licenses_registrations__expiring_heading", { replace: { license: licenseRegistrationAssociation } })
                                : t("licenses_registrations__expired_heading", { replace: { license: licenseRegistrationAssociation }  })
                        }
                    </div>
                </div>
                <div className="instruction">
                    {t('licenses_registrations__expiry_subheading')}
                </div>
            </div>
            <Button
                variant="text" 
                size="small" 
                onClick={() => onRenewClick(license.providerLicenseId)}
                className="renew-button"
            >
                {t('common:renew')}
            </Button>
        </div>
    );
}

const MultiLicenseAlertBody: FC<MultiLicenseAlertProps> = ({ licenses, type, onRenewClick }) => {
    const { t } = useTranslation(['providerprofile']);
    if (!licenses.length) return null;

    return (
        <Accordion className="multi-license-expiry-accordion">
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <div className="alert-content">
                    <div className="alert-text">
                        <div className="message-line">
                            <div className="message">
                            {
                                type === 'expiring'
                                    ? t("licenses_registrations__expiring_heading_multiple")
                                    : t("licenses_registrations__expired_heading_multiple")
                            }
                            </div>
                        </div>
                        <div className="instruction">
                            {t('licenses_registrations__expiry_subheading')}
                        </div>
                    </div>
                </div>
            </AccordionSummary>
            <AccordionDetails className="multi-license-expiry-details">
                {
                    licenses.map((lic) => (
                        <div className="multi-license-expiry-item" key={lic.providerLicenseId}>
                            <span>{lic.licenseRegistrationAssociation}</span>
                            <Button variant="text" onClick={() => onRenewClick(lic.providerLicenseId)}>{t('common:renew')}</Button>
                        </div>
                    ))
                }
            </AccordionDetails>
        </Accordion>
    );
};

export const LicensesAndRegistrationsAlert: FC<LicensesAndRegistrationsAlertProps> = ({ type, onRenewClick }) => {
    const { expiringLicenses, expiredLicenses, licenses } = useCredentialing();
    const isSingle = type === 'expiring' ? expiringLicenses.length === 1 : type === 'expired' ? expiredLicenses.length === 1 : undefined;

    const singleLicense = useMemo(() => {
        return licenses.find(lic => lic.providerLicenseId === (type==='expired' ? expiredLicenses?.[0] : expiringLicenses?.[0]))
    }, [licenses, expiringLicenses, expiredLicenses]);

    const multiLicense = useMemo(() => {
        return licenses.filter(lic => (type==='expired' ? expiredLicenses : expiringLicenses).includes(lic.providerLicenseId))
    }, [licenses, expiringLicenses, expiredLicenses]);

    return ((type === 'expiring' && expiringLicenses?.length) || (type === 'expired' && expiredLicenses?.length)) ?
    (
        <Alert
            className="expiry_license_warning" 
            variant="standard" 
            color={type === 'expired' ? "warning" : "info"}
            icon={type === 'expired' ? <WarningIcon color="warning" /> : <InfoIcon color="primary" />}
        >
            {isSingle ?
                (
                    <SingleLicenseAlertBody license={singleLicense} type={type} onRenewClick={onRenewClick} />
                ) : (
                    <MultiLicenseAlertBody licenses={multiLicense} type={type} onRenewClick={onRenewClick} />
                )
            }
        </Alert>
    ) : null;
}
